import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../lib/api';
import { APP_NAME, REFORGE_LOGO_SRC } from '../lib/config';
import { AccountMenu } from './AccountMenu';
import { AppFooter } from './AppFooter';
import { IdleLogoutModal, useIdleLogout } from '../hooks/useIdleLogout';

const primaryNav = [
  { path: '/dashboard', label: 'Dispatch', module: 'dashboard' },
  { path: '/map', label: 'Map', module: 'map' },
  { path: '/assignments', label: 'Assignments', module: 'assignments' },
  { path: '/alerts', label: 'Alerts', module: 'alerts' },
];

const secondaryNav = [
  { path: '/sites', label: 'Sites', module: 'sites' },
  { path: '/technicians', label: 'Technicians', module: 'technicians' },
  { path: '/users', label: 'Users', module: 'users' },
  { path: '/sessions', label: 'Sessions', module: 'sessions' },
  { path: '/assistant', label: 'Assistant', module: 'assistant' },
  { path: '/audit', label: 'Audit', module: 'audit' },
  { path: '/time-off', label: 'Time off', module: 'time_off' },
  { path: '/reports', label: 'Reports', module: 'reports' },
  { path: '/self-review', label: 'Self review', module: 'self_review' },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [isDemo, setIsDemo] = useState(user?.is_demo ?? false);
  const [openAlerts, setOpenAlerts] = useState<number | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [dockMoreOpen, setDockMoreOpen] = useState(false);
  const moreId = useId();
  const dockMoreId = useId();
  const moreRef = useRef<HTMLDivElement>(null);

  const idle = useIdleLogout();

  useEffect(() => {
    api
      .get<{ is_demo: boolean }>('/api/config')
      .then((cfg) => setIsDemo(cfg.is_demo))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const mods = user?.allowed_modules || [];
    if (user?.role !== 'admin' && !mods.includes('alerts')) return;
    let cancelled = false;
    const load = () => {
      api
        .get<Array<{ id: string }>>('/api/alerts')
        .then((alerts) => {
          if (!cancelled) setOpenAlerts(alerts.length);
        })
        .catch(() => undefined);
    };
    load();
    const timer = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [user?.role, user?.allowed_modules]);

  useEffect(() => {
    setMoreOpen(false);
    setDockMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    function onDoc(event: MouseEvent) {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [moreOpen]);

  const mods = user?.allowed_modules || [];
  const can = (module: string) => user?.role === 'admin' || mods.includes(module);
  const visiblePrimary = primaryNav.filter((item) => can(item.module));
  const moreNav = secondaryNav.filter((item) => can(item.module));
  const showMobileDock = visiblePrimary.length > 0 || moreNav.length > 0;
  const secondaryActive = moreNav.some((item) => location.pathname === item.path);
  const isMapPage = location.pathname === '/map';
  const homePath = can('dashboard')
    ? '/dashboard'
    : can('map')
      ? '/map'
      : can('self_review')
        ? '/self-review'
        : '/settings';

  return (
    <div
      className={`app-shell flex min-h-screen flex-col ${showMobileDock ? 'has-mobile-dock' : ''}`}
    >
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header className="sticky top-0 z-20 border-b border-white/10 bg-ink-950 text-white">
        <div className="mx-auto flex max-w-[100rem] flex-nowrap items-center gap-x-2 px-3 py-2 md:flex-wrap md:gap-x-4 md:px-4 md:py-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2 md:flex-none md:gap-3">
            <Link
              to={homePath}
              className="flex min-w-0 items-center gap-2.5 text-white"
              aria-label={`${APP_NAME} home`}
            >
              <span className="inline-flex shrink-0 items-center justify-center rounded bg-white p-1">
                <img
                  src={REFORGE_LOGO_SRC}
                  alt=""
                  className="h-8 w-auto object-contain sm:h-9"
                  width={72}
                  height={36}
                />
              </span>
              <span className="brand-mark truncate text-lg text-white sm:text-2xl">{APP_NAME}</span>
            </Link>
            {isDemo ? (
              <span className="demo-badge">Demo</span>
            ) : (
              <span className="live-badge">
                <span className="inline-block h-1.5 w-1.5 bg-emerald-400" aria-hidden="true" />
                Live
              </span>
            )}
          </div>

          <div className="ml-auto flex items-center gap-1 sm:order-last">
            <AccountMenu />
          </div>

          {(visiblePrimary.length > 0 || moreNav.length > 0) && (
            <nav
              className="hidden min-w-0 flex-1 items-center gap-1 md:flex"
              aria-label="Primary"
            >
              {visiblePrimary.map((item) => {
                const active = location.pathname === item.path;
                const alertCount = item.path === '/alerts' ? openAlerts : null;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-link ${active ? 'nav-link-active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    {item.label}
                    {alertCount != null && alertCount > 0 && (
                      <span className="ml-1.5 tabular-nums text-xs font-bold text-oxide-100">
                        {alertCount}
                        <span className="sr-only"> open</span>
                      </span>
                    )}
                  </Link>
                );
              })}
              {moreNav.length > 0 && (
                <div className="relative ml-1" ref={moreRef}>
                  <button
                    type="button"
                    className={`nav-link ${secondaryActive ? 'nav-link-active' : ''}`}
                    aria-current={secondaryActive ? 'page' : undefined}
                    aria-expanded={moreOpen}
                    aria-controls={moreId}
                    onClick={() => setMoreOpen((value) => !value)}
                  >
                    More
                  </button>
                  {moreOpen && (
                    <div
                      id={moreId}
                      className="absolute left-0 top-full z-30 mt-1 min-w-[11rem] border border-ink-100 bg-panel py-1 text-ink-900 shadow-lg"
                    >
                      {moreNav.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-3 py-2.5 text-sm font-medium hover:bg-ink-50 ${
                            location.pathname === item.path ? 'bg-ink-50 text-ink-950' : ''
                          }`}
                          onClick={() => setMoreOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>
          )}
        </div>

      </header>
      <main
        id="main-content"
        className={`mx-auto w-full flex-1 ${
          isMapPage
            ? 'max-w-[100rem] px-2 py-3 sm:px-3 sm:py-4'
            : 'max-w-[90rem] px-4 py-5 sm:py-7'
        }`}
      >
        {children}
      </main>
      <AppFooter compact={isMapPage} />
      {(visiblePrimary.length > 0 || moreNav.length > 0) && (
        <>
          {dockMoreOpen && moreNav.length > 0 && (
            <div id={dockMoreId} className="more-sheet" role="navigation" aria-label="More">
              {moreNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="more-sheet-link"
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                  onClick={() => setDockMoreOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}
          <nav className="mobile-dock" aria-label="Primary">
            {visiblePrimary.map((item) => {
              const active = location.pathname === item.path;
              const alertCount = item.path === '/alerts' ? openAlerts : null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobile-dock-link ${active ? 'is-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <span>{item.label}</span>
                  {alertCount != null && alertCount > 0 && (
                    <span className="mobile-dock-count">{alertCount}</span>
                  )}
                </Link>
              );
            })}
            {moreNav.length > 0 && (
              <button
                type="button"
                className={`mobile-dock-link ${secondaryActive || dockMoreOpen ? 'is-active' : ''}`}
                aria-expanded={dockMoreOpen}
                aria-controls={dockMoreId}
                onClick={() => setDockMoreOpen((value) => !value)}
              >
                <span>More</span>
              </button>
            )}
          </nav>
        </>
      )}
      <IdleLogoutModal
        warningSeconds={idle.warningSeconds}
        staySignedIn={idle.staySignedIn}
        signOutIdle={idle.signOutIdle}
      />
    </div>
  );
}
